/**
 * meta_microcopy_i18n.js
 * Single source of truth for all microcopy across the StreamSyncEngage stack
 *
 * Hierarchy: BUILDMODE > M5T_PRODUCT > WHITELABEL > LANG > TOKEN_MC
 *
 * Standardized Build Modes: LOCAL, DEV, SIT, QA, PROD
 *
 * Usage:
 *   import { MicrocopyManager } from './meta_microcopy_i18n.js';
 *   const copy = MicrocopyManager.get('welcome_back'); // Returns randomized if array
 *   const allTokens = MicrocopyManager.getAll('en');
 *
 * Future: Admin tool will persist changes back to this class via API
 */

export class MicrocopyManager {
  /**
   * Microcopy data structure
   * Format: {
   *   [BUILDMODE]: {  // LOCAL, DEV, SIT, QA, PROD
   *     [M5T_PRODUCT]: {
   *       [WHITELABEL]: {
   *         [LANG]: {
   *           [TOKEN_MC]: string | string[] (randomized if array)
   *         }
   *       }
   *     }
   *   }
   * }
   */
  static #data = {
    LOCAL: {
      SSE: {
        M5T: {
          en: {
            welcome_back: "Welcome back",
            please_enter_your_details: "Please enter your details",
            email: "Email",
            enter_your_email: "Enter your email",
            password: "Password",
            enter_your_password: "Enter your password",
            enter_email_and_password: "Enter email and password",
            sign_in: "Sign in",
            forgot_password: "Forgot password?",
            dont_have_an_account: "START YOUR FREE ACCOUNT",
            sign_up: "Sign up",
            please_enter_your_details_to_get_started: "Please enter your details to get started",
            already_have_an_account: "Already have an account?",
            log_in: "Log in",
            name: "Name",
            enter_your_name: "Enter your name",
            first_name: "First name",
            last_name: "Last name",
            company: "Company",
            phone: "Phone",
            confirm_password: "Confirm password",
            create_account: "Create account",
            home: "Home",
            dashboard: "Dashboard",
            correspondence: "Correspondence",
            settings: "Settings",
            sign_out: "Sign out",
            reset_password: "Reset password",
            new_password: "New password",
            confirm_new_password: "Confirm new password",
            enter_new_password: "Enter new password",
            enter_company_name: "Enter your company name",
            enter_phone_number: "Enter your phone number",
            reenter_password: "Re-enter your password",
            update_password: "Update password",
            update_your_password: "Update your account password",
            are_you_sure_sign_out: "Are you sure you want to sign out?",
            enter_email_for_reset: "Please enter your email to receive a password reset link",
            send_reset_link: "Send reset link",
            remember_password: "Remember your password?",
            cancel: "Cancel",
            save: "Save",
            delete: "Delete",
            edit: "Edit",
            loading: "Loading...",
            error: "Error",
            success: "Success",
            refresh: "Refresh",
            search: "Search",
            filter: "Filter",
            sort: "Sort",
            view: "View",
            close: "Close",
            submit: "Submit",
            back: "Back",
            next: "Next",
            previous: "Previous",
            confirm: "Confirm",
            yes: "Yes",
            no: "No",
            ok: "OK",

            // Validation messages
            validation_email_required: "Please enter your email",
            validation_email_invalid: "Please enter a valid email address",
            validation_password_required: "Please enter your password",
            validation_password_min_length: "Password must be at least 10 characters",
            validation_password_mismatch: "Passwords do not match",
            validation_first_name_required: "Please enter your first name",
            validation_last_name_required: "Please enter your last name",
            validation_new_password_required: "Please enter a new password",

            // Success messages
            success_welcome_back: "Welcome back!",
            success_password_reset: "Password reset successfully",
            success_signed_out: "Signed out successfully",
            success_reset_link_sent: "Password reset link sent!|Please check your email",

            // Error messages
            error_signin_failed: "Sign in failed",
            error_signup_failed: "Sign up failed",
            error_network: "Network error - please try again",
            error_password_reset_required: "Password reset required - redirecting...",

            // Loading states
            loading_signing_in: "SIGNING IN...",
            loading_creating_account: "Creating account...",
            loading_resetting_password: "Resetting password...",

            // Navigation - Primary
            nav_home: "Home",
            nav_home_tagline: "Dashboard and overview",

            // Navigation - Secondary (Home)
            nav_home_apps: "My Apps",
            nav_home_apps_tagline: "Your applications and integrations",
            nav_home_dash: "Dashboard",
            nav_home_dash_tagline: "Main dashboard and analytics",

            nav_engagements: "Engagements",
            nav_engagements_tagline: "Customer engagement tools and interactions",
            nav_develop: "Develop",
            nav_develop_tagline: "Development tools and resources",
            nav_profile: "Profile",
            nav_profile_tagline: "Account settings and preferences",

            // Navigation - Secondary (Engagements)
            nav_engagements_surveys: "Surveys",
            nav_engagements_surveys_tagline: "Create and manage customer surveys",
            nav_engagements_thumbs: "Feedback",
            nav_engagements_thumbs_tagline: "Collect thumbs up/down feedback",
            nav_engagements_voicemail: "Voicemail",
            nav_engagements_voicemail_tagline: "Voice message collection",
            nav_engagements_guidedtours: "Guided Tours",
            nav_engagements_guidedtours_tagline: "Interactive product tours",
            nav_engagements_slideshows: "Slideshows",
            nav_engagements_slideshows_tagline: "Content slideshows and presentations",
            nav_engagements_abtests: "A/B Tests",
            nav_engagements_abtests_tagline: "A/B testing campaigns",
            nav_engagements_questionnaires: "Questionnaires",
            nav_engagements_questionnaires_tagline: "Detailed questionnaires and forms",

            // Navigation - Secondary (Develop)
            nav_develop_documentation: "Documentation",
            nav_develop_documentation_tagline: "API and product documentation",
            nav_develop_devtools: "Dev Tools",
            nav_develop_devtools_tagline: "Developer tools and utilities",
            nav_develop_kbase: "Knowledge Base",
            nav_develop_kbase_tagline: "Technical knowledge base and articles",
            nav_develop_hackathon: "Hackathon",
            nav_develop_hackathon_tagline: "Hackathon events and challenges",
            nav_develop_partner: "Partner",
            nav_develop_partner_tagline: "Partner program and integrations",
            nav_develop_certification: "Certification",
            nav_develop_certification_tagline: "Developer certification programs",

            // Navigation - Secondary (Profile)
            nav_profile_aboutme: "About Me",
            nav_profile_aboutme_tagline: "View and edit your personal information",
            nav_profile_badges: "Badges",
            nav_profile_badges_tagline: "View your earned badges and achievements",
            nav_profile_language: "Language",
            nav_profile_language_tagline: "Manage language settings and preferences",
            nav_reset_password: "Reset Password",
            nav_reset_password_tagline: "Change your account password",
            nav_sign_out: "Sign Out",
            nav_sign_out_tagline: "Sign out of your account",

            // Navigation - Primary (Account)
            nav_account: "Account",
            nav_account_tagline: "Account management and support",

            // Navigation - Secondary (Account)
            nav_account_contact: "Contact",
            nav_account_contact_tagline: "Contact support and sales",
            nav_account_pricetier: "Price Tier",
            nav_account_pricetier_tagline: "View and manage pricing plans",
            nav_account_helpfaq: "Help & FAQ",
            nav_account_helpfaq_tagline: "Frequently asked questions and help",
            nav_account_team: "Team",
            nav_account_team_tagline: "Manage team members and permissions",
            nav_account_terms: "Terms",
            nav_account_terms_tagline: "Terms of service and legal agreements",

            // Admin Console Navigation
            nav_home: "Home",
            nav_home_tagline: "Admin console home dashboard",
            nav_entities: "Entities",
            nav_entities_tagline: "Entity and account management",
            nav_accounts: "Accounts",
            nav_accounts_tagline: "User account management",
            nav_groups: "Groups",
            nav_groups_tagline: "Group and role management",
            nav_invoices: "Invoices",
            nav_invoices_tagline: "Invoice and billing management",
            nav_ab_tests: "A/B Tests",
            nav_ab_tests_tagline: "A/B testing campaigns",
            nav_feedback: "Feedback",
            nav_feedback_tagline: "User feedback collection",
            nav_guided_tours: "Guided Tours",
            nav_guided_tours_tagline: "Interactive product tours",
            nav_questionnaires: "Questionnaires",
            nav_questionnaires_tagline: "Detailed questionnaires and forms",
            nav_slideshows: "Slideshows",
            nav_slideshows_tagline: "Content slideshows and presentations",
            nav_surveys: "Surveys",
            nav_surveys_tagline: "Survey campaigns and analytics",
            nav_voicemail: "Voicemail",
            nav_voicemail_tagline: "Voice message management",
            nav_ethercards: "EtherCards",
            nav_ethercards_tagline: "EtherCards platform management",
            nav_apps: "Apps",
            nav_apps_tagline: "Application management",
            nav_channels: "Channels",
            nav_channels_tagline: "Communication channels",
            nav_messages: "Messages",
            nav_messages_tagline: "Message management and templates",
            nav_content: "Content",
            nav_content_tagline: "Content and asset management",
            nav_rss: "RSS",
            nav_rss_tagline: "RSS feed management and syndication",
            nav_core_assets: "Core Assets",
            nav_core_assets_tagline: "Manage core application assets and resources",
            nav_deeplinks: "Deeplinks",
            nav_deeplinks_tagline: "Deep link management and tracking",
            nav_snippets: "Snippets",
            nav_snippets_tagline: "Code and content snippet management",
            nav_rollers: "Rollers",
            nav_rollers_tagline: "Rolling deployments and version management",
            nav_support: "Support",
            nav_support_tagline: "Support and monitoring tools",
            nav_dialog: "Dialog",
            nav_dialog_tagline: "Dialog and modal management",
            nav_meter: "Meter",
            nav_meter_tagline: "Usage metering and analytics",
            nav_metadata: "Metadata",
            nav_metadata_tagline: "Metadata and configuration",
            nav_i18n: "I18N",
            nav_i18n_tagline: "Internationalization maintenance",
            nav_meta_maint: "Meta Maint",
            nav_meta_maint_tagline: "Metadata maintenance and configuration",
            nav_silos: "Silos",
            nav_silos_tagline: "Data silo management",
            nav_cache: "Cache",
            nav_cache_tagline: "Cache management and statistics",
            nav_lifecycle: "Lifecycle",
            nav_lifecycle_tagline: "Data lifecycle and retention policies",
            nav_notifications: "Notifications",
            nav_notifications_tagline: "Notification management",
            nav_bell_drawer: "Bell Drawer",
            nav_bell_drawer_tagline: "Notification drawer configuration",
            nav_web_hooks: "Web Hooks",
            nav_web_hooks_tagline: "Webhook configuration and monitoring",
            nav_language: "Language",
            nav_language_tagline: "Change language preferences"
          },
          es: {
            welcome_back: "Bienvenido de nuevo",
            please_enter_your_details: "Por favor ingrese sus datos",
            email: "Correo electrónico",
            enter_your_email: "Ingrese su correo electrónico",
            password: "Contraseña",
            enter_your_password: "Ingrese su contraseña",
            enter_email_and_password: "Ingrese correo y contraseña",
            sign_in: "Iniciar sesión",
            forgot_password: "¿Olvidó su contraseña?",
            dont_have_an_account: "¿No tiene una cuenta?",
            sign_up: "Registrarse",
            please_enter_your_details_to_get_started: "Ingrese sus datos para comenzar",
            already_have_an_account: "¿Ya tiene una cuenta?",
            log_in: "Iniciar sesión",
            name: "Nombre",
            enter_your_name: "Ingrese su nombre",
            first_name: "Nombre",
            last_name: "Apellido",
            company: "Empresa",
            phone: "Teléfono",
            confirm_password: "Confirmar contraseña",
            create_account: "Crear cuenta",
            home: "Inicio",
            dashboard: "Panel",
            correspondence: "Correspondencia",
            settings: "Configuración",
            sign_out: "Cerrar sesión",
            reset_password: "Restablecer contraseña",
            new_password: "Nueva contraseña",
            confirm_new_password: "Confirmar nueva contraseña",
            enter_new_password: "Ingrese nueva contraseña",
            enter_company_name: "Ingrese el nombre de su empresa",
            enter_phone_number: "Ingrese su número de teléfono",
            reenter_password: "Vuelva a ingresar su contraseña",
            update_password: "Actualizar contraseña",
            update_your_password: "Actualice la contraseña de su cuenta",
            are_you_sure_sign_out: "¿Está seguro de que desea cerrar sesión?",
            enter_email_for_reset: "Ingrese su correo electrónico para recibir un enlace de restablecimiento de contraseña",
            send_reset_link: "Enviar enlace de restablecimiento",
            remember_password: "¿Recuerda su contraseña?",
            cancel: "Cancelar",
            save: "Guardar",
            delete: "Eliminar",
            edit: "Editar",
            loading: "Cargando...",
            error: "Error",
            success: "Éxito",
            refresh: "Actualizar",
            search: "Buscar",
            filter: "Filtrar",
            sort: "Ordenar",
            view: "Ver",
            close: "Cerrar",
            submit: "Enviar",
            back: "Atrás",
            next: "Siguiente",
            previous: "Anterior",
            confirm: "Confirmar",
            yes: "Sí",
            no: "No",
            ok: "OK",

            // Validation messages
            validation_email_required: "Por favor ingrese su correo electrónico",
            validation_email_invalid: "Por favor ingrese una dirección de correo válida",
            validation_password_required: "Por favor ingrese su contraseña",
            validation_password_min_length: "La contraseña debe tener al menos 10 caracteres",
            validation_password_mismatch: "Las contraseñas no coinciden",
            validation_first_name_required: "Por favor ingrese su nombre",
            validation_last_name_required: "Por favor ingrese su apellido",
            validation_new_password_required: "Por favor ingrese una nueva contraseña",

            // Success messages
            success_welcome_back: "¡Bienvenido de nuevo!",
            success_password_reset: "Contraseña restablecida exitosamente",
            success_signed_out: "Sesión cerrada exitosamente",
            success_reset_link_sent: "¡Enlace de restablecimiento enviado!|Por favor revise su correo electrónico",

            // Error messages
            error_signin_failed: "Error al iniciar sesión",
            error_signup_failed: "Error al registrarse",
            error_network: "Error de red - por favor intente de nuevo",
            error_password_reset_required: "Restablecimiento de contraseña requerido - redirigiendo...",

            // Loading states
            loading_signing_in: "INICIANDO SESIÓN...",
            loading_creating_account: "Creando cuenta...",
            loading_resetting_password: "Restableciendo contraseña...",

            // Navigation - Primary
            nav_home: "Inicio",
            nav_home_tagline: "Panel y resumen",

            // Navigation - Secondary (Home)
            nav_home_apps: "Mis Aplicaciones",
            nav_home_apps_tagline: "Sus aplicaciones e integraciones",
            nav_home_dash: "Panel",
            nav_home_dash_tagline: "Panel principal y análisis",

            nav_engagements: "Compromisos",
            nav_engagements_tagline: "Herramientas de interacción con clientes",
            nav_develop: "Desarrollar",
            nav_develop_tagline: "Herramientas y recursos de desarrollo",
            nav_profile: "Perfil",
            nav_profile_tagline: "Configuración y preferencias de cuenta",

            // Navigation - Secondary (Engagements)
            nav_engagements_surveys: "Encuestas",
            nav_engagements_surveys_tagline: "Crear y gestionar encuestas de clientes",
            nav_engagements_thumbs: "Retroalimentación",
            nav_engagements_thumbs_tagline: "Recopilar opiniones positivas y negativas",
            nav_engagements_voicemail: "Buzón de voz",
            nav_engagements_voicemail_tagline: "Recopilación de mensajes de voz",
            nav_engagements_guidedtours: "Recorridos guiados",
            nav_engagements_guidedtours_tagline: "Recorridos interactivos de productos",
            nav_engagements_slideshows: "Presentaciones",
            nav_engagements_slideshows_tagline: "Presentaciones y diapositivas de contenido",
            nav_engagements_abtests: "Pruebas A/B",
            nav_engagements_abtests_tagline: "Campañas de pruebas A/B",
            nav_engagements_questionnaires: "Cuestionarios",
            nav_engagements_questionnaires_tagline: "Cuestionarios y formularios detallados",

            // Navigation - Secondary (Develop)
            nav_develop_documentation: "Documentación",
            nav_develop_documentation_tagline: "Documentación de API y productos",
            nav_develop_devtools: "Herramientas Dev",
            nav_develop_devtools_tagline: "Herramientas y utilidades de desarrollo",
            nav_develop_kbase: "Base de Conocimientos",
            nav_develop_kbase_tagline: "Base de conocimientos y artículos técnicos",
            nav_develop_hackathon: "Hackathon",
            nav_develop_hackathon_tagline: "Eventos y desafíos de hackathon",
            nav_develop_partner: "Socio",
            nav_develop_partner_tagline: "Programa de socios e integraciones",
            nav_develop_certification: "Certificación",
            nav_develop_certification_tagline: "Programas de certificación para desarrolladores",

            // Navigation - Secondary (Profile)
            nav_profile_aboutme: "Sobre Mí",
            nav_profile_aboutme_tagline: "Ver y editar su información personal",
            nav_profile_badges: "Insignias",
            nav_profile_badges_tagline: "Ver sus insignias y logros obtenidos",
            nav_profile_language: "Idioma",
            nav_profile_language_tagline: "Gestionar configuración y preferencias de idioma",
            nav_reset_password: "Restablecer Contraseña",
            nav_reset_password_tagline: "Cambiar la contraseña de su cuenta",
            nav_sign_out: "Cerrar Sesión",
            nav_sign_out_tagline: "Cerrar sesión de su cuenta",

            // Navigation - Primary (Account)
            nav_account: "Cuenta",
            nav_account_tagline: "Gestión de cuenta y soporte",

            // Navigation - Secondary (Account)
            nav_account_contact: "Contacto",
            nav_account_contact_tagline: "Contactar con soporte y ventas",
            nav_account_pricetier: "Nivel de Precio",
            nav_account_pricetier_tagline: "Ver y gestionar planes de precios",
            nav_account_helpfaq: "Ayuda y FAQ",
            nav_account_helpfaq_tagline: "Preguntas frecuentes y ayuda",
            nav_account_team: "Equipo",
            nav_account_team_tagline: "Gestionar miembros del equipo y permisos",
            nav_account_terms: "Términos",
            nav_account_terms_tagline: "Términos de servicio y acuerdos legales",

            // Admin Console Navigation
            nav_home: "Inicio",
            nav_home_tagline: "Panel de inicio de la consola de administración",
            nav_silo_audits: "Auditorías de Silo",
            nav_silo_audits_tagline: "Auditar e inspeccionar datos de silo",
            nav_cache: "Caché",
            nav_cache_tagline: "Gestión de caché y estadísticas",
            nav_cache_stats: "Estadísticas",
            nav_cache_stats_tagline: "Estadísticas de rendimiento de caché",
            nav_dialog_links: "Enlaces de Diálogo",
            nav_dialog_links_tagline: "Gestionar configuraciones de enlaces de diálogo y modal",
            nav_i18n_maint: "Mant. I18n",
            nav_i18n_maint_tagline: "Mantenimiento de internacionalización y gestión de microcopia",
            nav_meta_maint: "Mant. Meta",
            nav_meta_maint_tagline: "Mantenimiento de metadatos y configuración",
            nav_bell_drawer: "Cajón de Campana",
            nav_bell_drawer_tagline: "Gestión de notificaciones y configuración del cajón de campana",
            nav_meter: "Medidor",
            nav_meter_tagline: "Medición de uso y analíticas",
            nav_deeplinks: "Enlaces Profundos",
            nav_deeplinks_tagline: "Gestión y seguimiento de enlaces profundos",
            nav_rss: "RSS",
            nav_rss_tagline: "Gestión de feeds RSS y sindicación",
            nav_core_assets: "Activos Principales",
            nav_core_assets_tagline: "Gestionar activos y recursos principales de la aplicación",
            nav_web_hooks: "Web Hooks",
            nav_web_hooks_tagline: "Configuración y monitoreo de webhooks",
            nav_snippets: "Fragmentos",
            nav_snippets_tagline: "Gestión de fragmentos de código y contenido",
            nav_snippets_sql: "SQL",
            nav_snippets_sql_tagline: "Fragmentos y plantillas de consultas SQL",
            nav_snippets_js: "JS",
            nav_snippets_js_tagline: "Fragmentos de código JavaScript",
            nav_rollers: "Rollers",
            nav_rollers_tagline: "Despliegues progresivos y gestión de versiones",
            nav_rollers_primary_nav: "Nav Primaria",
            nav_rollers_primary_nav_tagline: "Configuración del roller de navegación primaria"
          },
          "zh-Hans": {
            welcome_back: "欢迎回来",
            please_enter_your_details: "请输入您的详细信息",
            email: "电子邮件",
            enter_your_email: "输入您的电子邮件",
            password: "密码",
            enter_your_password: "输入您的密码",
            enter_email_and_password: "输入邮箱和密码",
            sign_in: "登入",
            forgot_password: "忘记密码？",
            dont_have_an_account: "没有账户？",
            sign_up: "报名",
            please_enter_your_details_to_get_started: "请输入您的详细信息以开始",
            already_have_an_account: "已有账户？",
            log_in: "登录",
            name: "姓名",
            enter_your_name: "输入您的姓名",
            first_name: "名字",
            last_name: "姓氏",
            company: "公司",
            phone: "电话",
            confirm_password: "确认密码",
            create_account: "创建账户",
            home: "首页",
            dashboard: "仪表板",
            correspondence: "信件",
            settings: "设置",
            sign_out: "登出",
            reset_password: "重置密码",
            new_password: "新密码",
            confirm_new_password: "确认新密码",
            enter_new_password: "输入新密码",
            enter_company_name: "输入您的公司名称",
            enter_phone_number: "输入您的电话号码",
            reenter_password: "重新输入您的密码",
            update_password: "更新密码",
            update_your_password: "更新您的账户密码",
            are_you_sure_sign_out: "您确定要登出吗？",
            enter_email_for_reset: "请输入您的电子邮件以接收密码重置链接",
            send_reset_link: "发送重置链接",
            remember_password: "记得您的密码？",
            cancel: "取消",
            save: "保存",
            delete: "删除",
            edit: "编辑",
            loading: "加载中...",
            error: "错误",
            success: "成功",
            refresh: "刷新",
            search: "搜索",
            filter: "筛选",
            sort: "排序",
            view: "查看",
            close: "关闭",
            submit: "提交",
            back: "返回",
            next: "下一个",
            previous: "上一个",
            confirm: "确认",
            yes: "是",
            no: "否",
            ok: "确定",

            // Validation messages
            validation_email_required: "请输入您的电子邮件",
            validation_email_invalid: "请输入有效的电子邮件地址",
            validation_password_required: "请输入您的密码",
            validation_password_min_length: "密码必须至少10个字符",
            validation_password_mismatch: "密码不匹配",
            validation_first_name_required: "请输入您的名字",
            validation_last_name_required: "请输入您的姓氏",
            validation_new_password_required: "请输入新密码",

            // Success messages
            success_welcome_back: "欢迎回来！",
            success_password_reset: "密码重置成功",
            success_signed_out: "成功登出",
            success_reset_link_sent: "密码重置链接已发送！|请检查您的电子邮件",

            // Error messages
            error_signin_failed: "登录失败",
            error_signup_failed: "注册失败",
            error_network: "网络错误 - 请重试",
            error_password_reset_required: "需要重置密码 - 正在重定向...",

            // Loading states
            loading_signing_in: "正在登录...",
            loading_creating_account: "正在创建账户...",
            loading_resetting_password: "正在重置密码...",

            // Navigation - Primary
            nav_home: "首页",
            nav_home_tagline: "仪表板和概览",

            // Navigation - Secondary (Home)
            nav_home_apps: "我的应用",
            nav_home_apps_tagline: "您的应用程序和集成",
            nav_home_dash: "仪表板",
            nav_home_dash_tagline: "主仪表板和分析",

            nav_engagements: "互动",
            nav_engagements_tagline: "客户互动工具和交互",
            nav_develop: "开发",
            nav_develop_tagline: "开发工具和资源",
            nav_profile: "个人资料",
            nav_profile_tagline: "账户设置和偏好",

            // Navigation - Secondary (Engagements)
            nav_engagements_surveys: "调查",
            nav_engagements_surveys_tagline: "创建和管理客户调查",
            nav_engagements_thumbs: "反馈",
            nav_engagements_thumbs_tagline: "收集赞成和反对意见",
            nav_engagements_voicemail: "语音信箱",
            nav_engagements_voicemail_tagline: "语音留言收集",
            nav_engagements_guidedtours: "引导式导览",
            nav_engagements_guidedtours_tagline: "交互式产品导览",
            nav_engagements_slideshows: "幻灯片",
            nav_engagements_slideshows_tagline: "内容幻灯片和演示",
            nav_engagements_abtests: "A/B测试",
            nav_engagements_abtests_tagline: "A/B测试活动",
            nav_engagements_questionnaires: "问卷",
            nav_engagements_questionnaires_tagline: "详细问卷和表单",

            // Navigation - Secondary (Develop)
            nav_develop_documentation: "文档",
            nav_develop_documentation_tagline: "API和产品文档",
            nav_develop_devtools: "开发工具",
            nav_develop_devtools_tagline: "开发者工具和实用程序",
            nav_develop_kbase: "知识库",
            nav_develop_kbase_tagline: "技术知识库和文章",
            nav_develop_hackathon: "黑客马拉松",
            nav_develop_hackathon_tagline: "黑客马拉松活动和挑战",
            nav_develop_partner: "合作伙伴",
            nav_develop_partner_tagline: "合作伙伴计划和集成",
            nav_develop_certification: "认证",
            nav_develop_certification_tagline: "开发者认证计划",

            // Navigation - Secondary (Profile)
            nav_profile_aboutme: "关于我",
            nav_profile_aboutme_tagline: "查看和编辑您的个人信息",
            nav_profile_badges: "徽章",
            nav_profile_badges_tagline: "查看您获得的徽章和成就",
            nav_profile_language: "语言",
            nav_profile_language_tagline: "管理语言设置和偏好",
            nav_reset_password: "重置密码",
            nav_reset_password_tagline: "更改您的账户密码",
            nav_sign_out: "登出",
            nav_sign_out_tagline: "退出您的账户",

            // Navigation - Primary (Account)
            nav_account: "账户",
            nav_account_tagline: "账户管理和支持",

            // Navigation - Secondary (Account)
            nav_account_contact: "联系",
            nav_account_contact_tagline: "联系支持和销售",
            nav_account_pricetier: "价格等级",
            nav_account_pricetier_tagline: "查看和管理定价计划",
            nav_account_helpfaq: "帮助和常见问题",
            nav_account_helpfaq_tagline: "常见问题和帮助",
            nav_account_team: "团队",
            nav_account_team_tagline: "管理团队成员和权限",
            nav_account_terms: "条款",
            nav_account_terms_tagline: "服务条款和法律协议",

            // Admin Console Navigation
            nav_home: "主页",
            nav_home_tagline: "管理控制台主页仪表板",
            nav_silo_audits: "仓库审计",
            nav_silo_audits_tagline: "审计和检查仓库数据",
            nav_cache: "缓存",
            nav_cache_tagline: "缓存管理和统计",
            nav_cache_stats: "统计",
            nav_cache_stats_tagline: "缓存性能统计",
            nav_dialog_links: "对话框链接",
            nav_dialog_links_tagline: "管理对话框和模态链接配置",
            nav_i18n_maint: "国际化维护",
            nav_i18n_maint_tagline: "国际化维护和微文案管理",
            nav_meta_maint: "元数据维护",
            nav_meta_maint_tagline: "元数据维护和配置",
            nav_bell_drawer: "铃铛抽屉",
            nav_bell_drawer_tagline: "通知管理和铃铛抽屉配置",
            nav_meter: "计量器",
            nav_meter_tagline: "使用计量和分析",
            nav_deeplinks: "深层链接",
            nav_deeplinks_tagline: "深层链接管理和跟踪",
            nav_rss: "RSS",
            nav_rss_tagline: "RSS订阅源管理和联合",
            nav_core_assets: "核心资产",
            nav_core_assets_tagline: "管理核心应用程序资产和资源",
            nav_web_hooks: "网络钩子",
            nav_web_hooks_tagline: "Webhook配置和监控",
            nav_snippets: "代码片段",
            nav_snippets_tagline: "代码和内容片段管理",
            nav_snippets_sql: "SQL",
            nav_snippets_sql_tagline: "SQL查询片段和模板",
            nav_snippets_js: "JS",
            nav_snippets_js_tagline: "JavaScript代码片段",
            nav_rollers: "滚动器",
            nav_rollers_tagline: "滚动部署和版本管理",
            nav_rollers_primary_nav: "主导航",
            nav_rollers_primary_nav_tagline: "主导航滚动器配置"
          }
        }
      }
    },
    SIT: {
      SSE: {
        M5T: {
          en: {
            welcome_back: ["Welcome back", "Good to see you again", "Hello again"],
            please_enter_your_details: "Please enter your details",
            email: "Email",
            enter_your_email: "Enter your email",
            password: "Password",
            enter_your_password: "Enter your password",
            sign_in: "Sign in"
          }
        }
      }
    },
    UAT: {
      SSE: {
        M5T: {
          en: {
            welcome_back: "Welcome back",
            please_enter_your_details: "Please enter your details",
            email: "Email",
            enter_your_email: "Enter your email",
            password: "Password",
            enter_your_password: "Enter your password",
            sign_in: "Sign in"
          }
        }
      }
    },
    QA: {
      SSE: {
        M5T: {
          en: {
            welcome_back: "Welcome back",
            please_enter_your_details: "Please enter your details",
            email: "Email",
            enter_your_email: "Enter your email",
            password: "Password",
            enter_your_password: "Enter your password",
            sign_in: "Sign in"
          }
        }
      }
    },
    PROD: {
      SSE: {
        M5T: {
          en: {
            welcome_back: "Welcome back",
            please_enter_your_details: "Please enter your details",
            email: "Email",
            enter_your_email: "Enter your email",
            password: "Password",
            enter_your_password: "Enter your password",
            sign_in: "Sign in"
          }
        }
      }
    }
  };

  // Current context (overridable via setContext)
  static #context = {
    buildMode: 'DEV',
    product: 'SSE',
    whitelabel: 'M5T',
    lang: 'en'
  };

  /**
   * Set the current context for microcopy retrieval
   * @param {Object} context - Context object
   * @param {string} context.buildMode - Build mode (DEV, SIT, UAT, QA, PROD)
   * @param {string} context.product - Product name (SSE, etc.)
   * @param {string} context.whitelabel - Whitelabel name (M5T, etc.)
   * @param {string} context.lang - Language code (en, es, zh-Hans, etc.)
   */
  static setContext({ buildMode, product, whitelabel, lang }) {
    if (buildMode) this.#context.buildMode = buildMode;
    if (product) this.#context.product = product;
    if (whitelabel) this.#context.whitelabel = whitelabel;
    if (lang) this.#context.lang = lang;
  }

  /**
   * Get the current context
   * @returns {Object} Current context
   */
  static getContext() {
    return { ...this.#context };
  }

  /**
   * Get microcopy by token
   * @param {string} token - Microcopy token (e.g., 'welcome_back')
   * @param {Object} overrideContext - Optional context override
   * @returns {string|null} Microcopy text (randomized if array), or null if not found
   */
  static get(token, overrideContext = {}) {
    const ctx = { ...this.#context, ...overrideContext };
    const { buildMode, product, whitelabel, lang } = ctx;

    try {
      const value = this.#data[buildMode]?.[product]?.[whitelabel]?.[lang]?.[token];

      if (!value) {
        console.warn(`[MicrocopyManager] Token not found: ${token} (${buildMode}/${product}/${whitelabel}/${lang})`);
        return null;
      }

      // If array, randomize
      if (Array.isArray(value)) {
        return value[Math.floor(Math.random() * value.length)];
      }

      return value;
    } catch (err) {
      console.error(`[MicrocopyManager] Error retrieving token: ${token}`, err);
      return null;
    }
  }

  /**
   * Get all microcopy for a given context
   * @param {Object} overrideContext - Optional context override
   * @returns {Object|null} All microcopy tokens, or null if not found
   */
  static getAll(overrideContext = {}) {
    const ctx = { ...this.#context, ...overrideContext };
    const { buildMode, product, whitelabel, lang } = ctx;

    try {
      return this.#data[buildMode]?.[product]?.[whitelabel]?.[lang] || null;
    } catch (err) {
      console.error(`[MicrocopyManager] Error retrieving all tokens`, err);
      return null;
    }
  }

  /**
   * Check if a token exists
   * @param {string} token - Microcopy token
   * @param {Object} overrideContext - Optional context override
   * @returns {boolean} True if token exists
   */
  static has(token, overrideContext = {}) {
    const ctx = { ...this.#context, ...overrideContext };
    const { buildMode, product, whitelabel, lang } = ctx;

    try {
      return !!this.#data[buildMode]?.[product]?.[whitelabel]?.[lang]?.[token];
    } catch (err) {
      return false;
    }
  }

  /**
   * Get all available languages for current context
   * @returns {string[]} Array of language codes
   */
  static getAvailableLanguages() {
    const { buildMode, product, whitelabel } = this.#context;
    try {
      return Object.keys(this.#data[buildMode]?.[product]?.[whitelabel] || {});
    } catch (err) {
      return [];
    }
  }

  /**
   * Update microcopy (for admin tool)
   * @param {string} token - Microcopy token
   * @param {string|string[]} value - New value
   * @param {Object} overrideContext - Optional context override
   * @returns {boolean} Success
   */
  static update(token, value, overrideContext = {}) {
    const ctx = { ...this.#context, ...overrideContext };
    const { buildMode, product, whitelabel, lang } = ctx;

    try {
      if (!this.#data[buildMode]) this.#data[buildMode] = {};
      if (!this.#data[buildMode][product]) this.#data[buildMode][product] = {};
      if (!this.#data[buildMode][product][whitelabel]) this.#data[buildMode][product][whitelabel] = {};
      if (!this.#data[buildMode][product][whitelabel][lang]) this.#data[buildMode][product][whitelabel][lang] = {};

      this.#data[buildMode][product][whitelabel][lang][token] = value;
      return true;
    } catch (err) {
      console.error(`[MicrocopyManager] Error updating token: ${token}`, err);
      return false;
    }
  }

  /**
   * Delete microcopy (for admin tool)
   * @param {string} token - Microcopy token
   * @param {Object} overrideContext - Optional context override
   * @returns {boolean} Success
   */
  static delete(token, overrideContext = {}) {
    const ctx = { ...this.#context, ...overrideContext };
    const { buildMode, product, whitelabel, lang } = ctx;

    try {
      delete this.#data[buildMode]?.[product]?.[whitelabel]?.[lang]?.[token];
      return true;
    } catch (err) {
      console.error(`[MicrocopyManager] Error deleting token: ${token}`, err);
      return false;
    }
  }

  /**
   * Export all data (for admin tool)
   * @returns {Object} Complete microcopy data
   */
  static exportData() {
    return JSON.parse(JSON.stringify(this.#data));
  }

  /**
   * Import data (for admin tool)
   * @param {Object} data - Microcopy data to import
   * @returns {boolean} Success
   */
  static importData(data) {
    try {
      this.#data = data;
      return true;
    } catch (err) {
      console.error(`[MicrocopyManager] Error importing data`, err);
      return false;
    }
  }
}

// CommonJS export for server-side compatibility
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { MicrocopyManager };
}
